import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import duchessLogo from '../../assets/ducchessCollection.webp';
import farmshopLogo from '../../assets/farmshopLogo.webp';
import belvoirBooksCover from '../../assets/books/The-Accidental-Duchess-asset.png';

const TopNavContainer = styled.nav`
  position: relative;
  /* Above main NavContainer (z-index: 3) so this strip and its mobile dropdown paint over the logo row */
  z-index: 5;
  background-color: ${props => props.theme.colors.primary};
  padding: ${props =>
    props.compact
      ? '0.35rem clamp(1rem, 4vw, 8rem)'
      : '0.5rem clamp(1rem, 4vw, 8rem)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: ${props =>
    props.$navScrolled
      ? '0 4px 28px rgba(0, 0, 0, 0.22)'
      : 'inset 0 -1px 0 rgba(0, 0, 0, 0.06)'};
  transition: box-shadow 0.35s ease, padding 0.35s ease;

  @media (max-width: 768px) {
    padding: 0.45rem 1rem;
    justify-content: space-between;
    position: relative;
  }
`;

const TopNavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  padding: 0.4rem 0.55rem;
  transition: color 0.2s ease, transform 0.2s ease, opacity 0.2s ease;

  &.active {
    color: ${props => props.theme.colors.accent};
  }

  @media (hover: hover) {
    &:hover {
      color: #fff;
      opacity: 0.95;
      transform: translateY(-1px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.85);
    outline-offset: 4px;
    border-radius: 2px;
  }
`;

const MenuBar = styled.span`
  display: block;
  width: 20px;
  height: 2px;
  background: #fff;
  border-radius: 1px;
`;

const MenuButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  cursor: pointer;
  padding: 0.55rem 0.65rem;
  margin: 0;

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.85);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const NavLinksContainer = styled.div`
  display: flex;
  gap: clamp(0.75rem, 2vw, 1.75rem);
  align-items: center;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    align-items: stretch;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(16, 29, 64, 0.98);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    padding: 1rem 1.25rem 1.25rem;
    gap: 0.35rem;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const ShopButton = styled.button`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.55rem 1.2rem;
  line-height: 1;
  cursor: pointer;
  color: #fff;
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 999px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: background 0.25s ease, border-color 0.25s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    border-color: #fff;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.85);
    outline-offset: 3px;
  }

  @media (prefers-reduced-transparency: reduce) {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background: transparent;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile-only shop button (visible only under 768px)
const MobileShopButton = styled(ShopButton)`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 960px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem;
    gap: 1rem;
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;

  h2 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.75rem;
    margin: 0 0 0.25rem 0;
    color: ${props => props.theme.colors.primary};
  }

  p {
    margin: 0;
    color: #666;
    font-family: ${props => props.theme.fonts.main};
    font-size: 1rem;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StoreLogoWrap = styled.div`
  height: 120px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    height: 100px;
  }
`;

const StoreLogo = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: 0;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.18));

  @media (max-width: 768px) {
    width: 100px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ShopOption = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid transparent;
  border-radius: 8px;
  text-decoration: none;
  color: ${props => props.theme.colors.primary};
  transition: all 0.3s ease;
  min-height: 200px;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 1rem 0;
    text-align: center;
  }

  p {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1rem;
    color: #666;
    text-align: center;
    margin: 0;
  }

  @media (max-width: 768px) {
    min-height: 150px;
    padding: 1.5rem;

    h3 {
      font-size: 1.25rem;
    }

    p {
      font-size: 0.9rem;
    }
  }
`;

const DisabledShopOption = styled(ShopOption).attrs({
  as: 'div',
  role: 'link',
  tabIndex: -1,
  'aria-disabled': 'true',
})`
  background: linear-gradient(135deg, #e5e5e5 0%, #d6d8db 100%);
  filter: grayscale(100%);
  opacity: 0.6;
  cursor: not-allowed;
  border-color: rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: rgba(0, 0, 0, 0.05);
    transform: none;
    box-shadow: none;
  }

  h3,
  p {
    color: #555;
  }
`;

const ComingSoonOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  pointer-events: none;
`;

const TicketsNote = styled.div`
  margin-top: 1rem;
  text-align: center;
  color: #666;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.95rem;
`;

const TicketsLink = styled(Link)`
  color: ${props => props.theme.colors.accent};
  text-decoration: underline;
  margin-left: 0.35rem;
  &:hover { opacity: 0.9; }
`;

const TopNav = ({ currentSection, onSectionChange, compact, navScrolled = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(currentSection);
  const [isShopModalOpen, setIsShopModalOpen] = useState(false);

  // Update active section when currentSection prop changes
  useEffect(() => {
    setActiveSection(currentSection);
  }, [currentSection]);

  // Listen for custom events to force section
  useEffect(() => {
    const handleForceRetailSection = (event) => {
      if (event.detail && event.detail.section === 'retail') {
        setActiveSection('retail');
        onSectionChange('retail');
      }
    };

    const handleForceStaySection = (event) => {
      if (event.detail && event.detail.section === 'stay') {
        setActiveSection('stay');
        onSectionChange('stay');
      }
    };

    // Add event listeners
    document.addEventListener('forceRetailSection', handleForceRetailSection);
    document.addEventListener('forceStaySection', handleForceStaySection);

    // Clean up
    return () => {
      document.removeEventListener('forceRetailSection', handleForceRetailSection);
      document.removeEventListener('forceStaySection', handleForceStaySection);
    };
  }, [onSectionChange]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    onSectionChange(section);
    setIsMenuOpen(false);
  };

  const openShopModal = () => {
    setIsShopModalOpen(true);
  };

  const closeShopModal = () => {
    setIsShopModalOpen(false);
  };

  const handleShopOptionClick = (shopType) => {
    closeShopModal();
    // Force the retail section when clicking on shop options
    if (shopType === 'farm-shop' || shopType === 'duchess-collection' || shopType === 'belvoir-books') {
      setActiveSection('retail');
      onSectionChange('retail');
    }
  };

  return (
    <>
      <TopNavContainer compact={compact} $navScrolled={navScrolled}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.75rem, 2vw, 1.5rem)' }}>
          <MenuButton
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="topnav-section-links"
            aria-label="Site sections menu"
          >
            <MenuBar aria-hidden="true" />
            <MenuBar aria-hidden="true" />
            <MenuBar aria-hidden="true" />
          </MenuButton>
          <NavLinksContainer id="topnav-section-links" isOpen={isMenuOpen}>
            <TopNavLink 
              to="/castle" 
              onClick={() => handleSectionChange('castle')}
              className={activeSection === 'castle' ? 'active' : ''}
            >
              THE CASTLE
            </TopNavLink>
            <TopNavLink 
              to="/retail-village" 
              onClick={() => handleSectionChange('retail')}
              className={activeSection === 'retail' ? 'active' : ''}
            >
              RETAIL VILLAGE
            </TopNavLink>
            <TopNavLink 
              to="/stay" 
              onClick={() => handleSectionChange('stay')}
              className={activeSection === 'stay' ? 'active' : ''}
            >
              STAY
            </TopNavLink>
            <TopNavLink 
              to="/christmas" 
              onClick={() => handleSectionChange('christmas')}
              className={activeSection === 'christmas' ? 'active' : ''}
            >
              CHRISTMAS
            </TopNavLink>
            {/* Mobile-visible Shop Online button */}
            <MobileShopButton
              type="button"
              onClick={() => {
                openShopModal();
                setIsMenuOpen(false);
              }}
            >
              SHOP ONLINE
            </MobileShopButton>
          </NavLinksContainer>
        </div>
        <ShopButton type="button" onClick={openShopModal}>
          SHOP ONLINE
        </ShopButton>
      </TopNavContainer>

      <ModalOverlay isOpen={isShopModalOpen} onClick={closeShopModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={closeShopModal}>×</CloseButton>
          <ModalHeader>
            <h2>Shop Online</h2>
            <p>Select a store to continue</p>
          </ModalHeader>
          <OptionsGrid>
            <ShopOption 
              to="/farm-shop" 
              onClick={() => handleShopOptionClick('farm-shop')}
            >
              <StoreLogoWrap>
                <StoreLogo src={farmshopLogo} alt="Belvoir Farm Shop" />
              </StoreLogoWrap>
              <h3>Enter Farm Shop</h3>
              <p>Fresh, local produce from our estate</p>
            </ShopOption>
            <DisabledShopOption>
              <ComingSoonOverlay>Coming Soon</ComingSoonOverlay>
              <StoreLogoWrap>
                <StoreLogo src={duchessLogo} alt="The Duchess Collection" />
              </StoreLogoWrap>
              <h3>Enter Duchess Collection</h3>
              <p>Refined fashion and homeware</p>
            </DisabledShopOption>
            <ShopOption
              to="/about/belvoir-books"
              onClick={() => handleShopOptionClick('belvoir-books')}
            >
              <StoreLogoWrap>
                <StoreLogo src={belvoirBooksCover} alt="Belvoir Books" />
              </StoreLogoWrap>
              <h3>Belvoir Books</h3>
              <p>Stories of the castle, the estate, and the landscape—order for delivery</p>
            </ShopOption>
          </OptionsGrid>
          <TicketsNote>
            Looking for tickets?
            <TicketsLink to="/your-visit/day-tickets">Click here</TicketsLink>
          </TicketsNote>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default TopNav; 